/**
 * React Query hooks for /agreement-users (#276, 2026-06-08).
 *
 * Writes return WriteResult<AgreementUser> — the same envelope used by
 * every other entity that flows through maker-checker. Today Compliance
 * and Admin auto-apply (r.pending=false, r.entity populated, no CR);
 * RM/read-only roles get 403 from the ComplianceUser dependency. The
 * WriteResult shape is unchanged across those branches so the Save
 * aggregation in AgreementDetailPage already handles the future case
 * where /agreement-users widens to RM (Task 1 future-proof, 2026-06-09).
 *
 * Cache discipline mirrors useAgreements: only seed the detail cache
 * when r.pending is false, so live readers don't see optimistic data
 * for queued writes.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { agreementUsersApi, type AgreementUsersListParams } from '../api/agreementUsers';
import { ApiError } from '../api/client';
import type {
  AgreementUser,
  AgreementUserCreateInput,
  AgreementUserUpdateInput,
  Page,
  UUID,
  WriteResult,
} from '../api/types';

export const agreementUserKeys = {
  all:    ['agreement-users'] as const,
  list:   (p: AgreementUsersListParams) =>
    ['agreement-users', 'list', p] as const,
  detail: (id: UUID) =>
    ['agreement-users', 'detail', id] as const,
};

export function useAgreementUsers(params: AgreementUsersListParams) {
  return useQuery<Page<AgreementUser>, ApiError>({
    queryKey: agreementUserKeys.list(params),
    queryFn: () => agreementUsersApi.list(params),
  });
}

export function useAgreementUser(id: UUID | undefined) {
  return useQuery<AgreementUser, ApiError>({
    queryKey: agreementUserKeys.detail(id ?? ''),
    queryFn: () => agreementUsersApi.get(id!),
    enabled: !!id,
  });
}

export function useCreateAgreementUser() {
  const qc = useQueryClient();
  return useMutation<WriteResult<AgreementUser>, ApiError, AgreementUserCreateInput>({
    mutationFn: agreementUsersApi.create,
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: agreementUserKeys.all });
      if (!r.pending) qc.setQueryData(agreementUserKeys.detail(r.entity.id), r.entity);
    },
  });
}

export function useUpdateAgreementUser(id: UUID) {
  const qc = useQueryClient();
  return useMutation<WriteResult<AgreementUser>, ApiError, AgreementUserUpdateInput>({
    mutationFn: (p) => agreementUsersApi.update(id, p),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: agreementUserKeys.all });
      if (!r.pending) qc.setQueryData(agreementUserKeys.detail(id), r.entity);
    },
  });
}

export function useDeleteAgreementUser() {
  const qc = useQueryClient();
  return useMutation<WriteResult<AgreementUser>, ApiError, UUID>({
    mutationFn: agreementUsersApi.delete,
    onSuccess: () => { qc.invalidateQueries({ queryKey: agreementUserKeys.all }); },
  });
}

export function useRestoreAgreementUser() {
  const qc = useQueryClient();
  return useMutation<WriteResult<AgreementUser>, ApiError, UUID>({
    mutationFn: agreementUsersApi.restore,
    onSuccess: () => { qc.invalidateQueries({ queryKey: agreementUserKeys.all }); },
  });
}
